import { lazy, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ContactGuard } from "@/components/ContactGuard/ContactGuard";
import { NotFound } from "@/components/not-found";
import Layout from "@/layout/mainlayout";

const Contacts = lazy(() =>
  import("./contacts").then((module) => ({ default: module.ContactsPage }))
);
const Dashboard = lazy(() =>
  import("./dashboard").then((module) => ({ default: module.DashboardPage }))
);
const Reports = lazy(() =>
  import("./reports").then((module) => ({ default: module.ReportsPage }))
);
const Documents = lazy(() =>
  import("./documents").then((module) => ({ default: module.DocumentsPage }))
);
const Securities = lazy(() =>
  import("./securities").then((module) => ({ default: module.SecuritiesPage }))
);
const Withdrawals = lazy(() =>
  import("./withdrawals").then((module) => ({
    default: module.WithdrawalsPage,
  }))
);
const Contact = lazy(() =>
  import("./[contactId]").then((module) => ({ default: module.ContactPage }))
);
const Prospect = lazy(() =>
  import("./[prospectId]").then((module) => ({ default: module.ProspectPage }))
);
const Security = lazy(() =>
  import("./[securityId]").then((module) => ({ default: module.SecurityPage }))
);
const Positions = lazy(() =>
  import("./positions").then((module) => ({ default: module.PositionsPage }))
);
const Trader = lazy(() =>
  import("./trader").then((module) => ({ default: module.TraderPage }))
);
const Docprodtest = lazy(() =>
  import("./docprodtest").then((module) => ({
    default: module.DocProdTestPage,
  }))
);

const MeetingRooms = lazy(() =>
  import("./meetingrooms").then((module) => ({
    default: module.MeetingroomPage,
  }))
);
const Transactions = lazy(() =>
  import("./transactions").then((module) => ({
    default: module.TransactionsPage,
  }))
);
const Ledger = lazy(() =>
  import("./ledger").then((module) => ({ default: module.LedgerPager }))
);
const Queries = lazy(() =>
  import("./queries").then((module) => ({ default: module.QueriesPage }))
);
const Portfolios = lazy(() =>
  import("./portfolios").then((module) => ({
    default: module.PortfoliosPage,
  }))
);
const HolidayCalendar = lazy(() =>
  import("./holiday-calendar").then((module) => ({
    default: module.HolidayCalendarPage,
  }))
);
const ModelPortfolios = lazy(() =>
  import("./modelportfolios").then((module) => ({
    default: module.ModelPortfoliosPage,
  }))
);
const Prospects = lazy(() =>
  import("./prospects").then((module) => ({ default: module.ProspectsPage }))
);
const QualityControl = lazy(() =>
  import("./quality-control").then((module) => ({
    default: module.QualityControlPage,
  }))
);
const Chat = lazy(() =>
  import("./chat").then((module) => ({ default: module.ChatPage }))
);
const OfferDocument = lazy(() =>
  import("./offer").then((module) => ({
    default: module.OfferDocumentPage,
  }))
);

const Form = lazy(() =>
  import("./form").then((module) => ({ default: module.FormPage }))
);

const routeTitles: Record<string, string> = {
  "": "Dashboard",
  dashboard: "Dashboard",
  contacts: "Contacts",
  quality: "Quality Control",
  reports: "Reports",
  documents: "Documents",
  positions: "Positions",
  portfolios: "Portfolios",
  transactions: "Transactions",
  securities: "Securities",
  prospects: "Prospects",
  queries: "Queries",
  addprov: "Addprov",
  trader: "Trader",
  ledger: "Ledger",
  offer: "Offer Document",
  holiday: "Holiday Calendar",
  meetingrooms: "Meeting Rooms",
  modelportfolios: "Model Portfolios",
};

const TitleUpdater = () => {
  const location = useLocation().pathname.split("/")[1];
  useEffect(() => {
    document.title = routeTitles[location]
      ? "TRA | " + routeTitles[location]
      : "TRA | The Real Advisor";
  }, [location]);

  return null;
};

export const UserWithLinkedContactRoutes = () => {
  return (
    <>
      <TitleUpdater />
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route path="" element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="offer" element={<OfferDocument />} />
          <Route path="positions" element={<Positions />} />
          <Route path="transactions" element={<Transactions />} />
          <Route path="holiday" element={<HolidayCalendar />} />
          <Route path="portfolios" element={<Portfolios />} />
          <Route path="meetingrooms" element={<MeetingRooms />} />
          <Route path="queries" element={<Queries />} />
          <Route path="modelportfolios" element={<ModelPortfolios />} />
          <Route path="ledger" element={<Ledger />} />
          <Route path="withdrawals" element={<Withdrawals />} />
          <Route path="trader" element={<Trader />} />
          <Route path="chat" element={<Chat />} />
          <Route path="contacts" element={<Contacts />} />
          <Route path="docprodtest" element={<Docprodtest />} />
          <Route path="contacts">
            <Route
              path=":contactId"
              element={
                <ContactGuard>
                  <Contact />
                </ContactGuard>
              }
            />
          </Route>
          <Route path="quality" element={<QualityControl />} />
          <Route path="prospects" element={<Prospects />} />
          <Route path="prospects">
            <Route path=":prospectId" element={<Prospect />} />
          </Route>
          <Route path="reports" element={<Reports />} />
          <Route path="documents" element={<Documents />} />
          <Route path="securities" element={<Securities />} />
          <Route path="securities">
            <Route path=":securityId" element={<Security />} />
          </Route>
          <Route path="form">
            <Route path=":formId" element={<Form />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </>
  );
};
